import { gql } from '@apollo/client';

/**
 * 地址异常上报 / 编辑（2.2 需求点1）。
 *
 * <p>运营判定该地址上门必失败（马路地址、无门牌号等）→ 免司机上门，直接进二次建联。
 * 已上报再次提交即编辑（覆盖原因与凭证，站点侧另追加一条 edit 日志）。
 *
 * <p>`reviewPoolId` 与 `cooperationWarehouseId` **二选一**：
 * 审核池里站点还没建 → 传前者（落在条目暂存上，建站时由后端搬到站点）；
 * 司机反馈跟进里站点已存在且处于「待上门」→ 传后者。
 *
 * <p><b>失败形态有两种，别只处理一种：</b>
 * ① **业务校验失败**（原因/凭证缺失、状态不允许、灰度未开放）→ api-server 的 `orLogFailure`
 * 记日志后返回 `success: false`，**不抛 GraphQL error**，messageKey 只落在服务端日志里，
 * 界面目前只能弹通用「操作失败」；② pickup-service 真抛异常 → 走 GraphQL error，
 * 那条路才能按 messageKey 给提示。想让 ① 的具体原因也到运营眼前，需要 api-server 改成透传
 * messageKey（本次未做，记技术债）。
 *
 * <p><b>必须选 proofDisplayUrls</b>：前端手里只有上传拿到的 objectKey，预签名可读地址只有服务端能签。
 * 拿不到它，上报后那张凭证缩略图就只能退化成占位块 —— 早先是靠「上报成功后整体重拉列表第 1 页」
 * 间接把地址取回来的，副作用是列表被重置、运营滚到第 2 页之后上报会丢卡片。现在随 mutation 一起返回，
 * 调用方就能只就地更新选中条目、完全不动列表。
 */
export const REPORT_ADDRESS_ABNORMAL = gql`
  mutation ReportAddressAbnormal($input: ReportAddressAbnormalInput!) {
    reportAddressAbnormal(input: $input) {
      success
      proofDisplayUrls
    }
  }
`;

/**
 * 撤销地址异常上报（2.2 需求点1）。
 *
 * <p><b>只在二次建联结果提交之前有效</b>：站点一旦被置为「确认无法建联」，状态机没有回退，
 * 撤销上报也改不回站点状态 —— 后端会拒绝，此时应引导运营走恢复揽收（补群 ID + 群名转已建联）。
 * 所以界面在站点已进入该状态后必须把撤销入口置灰，而不是让运营点了才发现撤不掉。
 */
export const REVOKE_ADDRESS_ABNORMAL = gql`
  mutation RevokeAddressAbnormal($input: ReportAddressAbnormalInput!) {
    revokeAddressAbnormal(input: $input)
  }
`;

export interface ReportAddressAbnormalInput {
  /** 审核池条目 id（站点尚未创建时传）。与 cooperationWarehouseId 二选一 */
  reviewPoolId?: number;
  /** 站点 id（司机反馈跟进：站点已存在且处于「待上门」时传） */
  cooperationWarehouseId?: number;
  /** 异常原因说明：自由输入，**非必填**；撤销时不用传 */
  reason?: string;
  /** 备注：自由输入，**非必填**；撤销时不用传 */
  remark?: string;
  /**
   * 异常凭证 objectKey 列表，**必填**。
   * 原因与备注都非必填 ⇒ 凭证是这条上报唯一必然存在的证据，后端会校验并返回
   * PICKUP_ABNORMAL_PROOF_REQUIRED。
   */
  proofS3Keys?: string[];
}

export interface ReportAddressAbnormalResult {
  success: boolean;
  /**
   * 本次提交凭证的预签名可读地址，供就地渲染缩略图。
   *
   * <p><b>不保证与入参 proofS3Keys 等长</b>：个别 key 预签名失败会被服务端跳过（那一张不展示）。
   * 消费方要么整组用它、要么整组不用 —— 不要按下标与 proofS3Keys 配对后补空串，
   * OnsiteProofs 是「urls 非空就整组按 urls 渲染」，混入空串会渲染成裂图。
   */
  proofDisplayUrls: string[];
}

export interface ReportAddressAbnormalResponse {
  reportAddressAbnormal: ReportAddressAbnormalResult;
}

export interface RevokeAddressAbnormalResponse {
  revokeAddressAbnormal: boolean;
}

/**
 * 异常原因的**建议项**（不是白名单）。
 *
 * <p>原型上这一栏是「请输入 / 选择」—— 可自由输入、也可从建议里挑。刻意不做成枚举校验：
 * 现场情况穷举不完，限制取值只会逼运营选一个最接近的、反而失真。后端不校验取值，
 * 所以这里增删条目<b>不需要改后端</b>，也不再存在"前端比后端旧就认不出码"的问题。
 *
 * <p>2026-09-09 产品定稿的四类场景，**整体替换**了此前的「马路地址 / 无门牌号」两项
 * —— 新的「公路/桥梁地址」「家庭仓（无门牌号）」与旧两项语义重叠，并存会让下拉里出现
 * 两组近义项、运营选哪个都对，反而把数据打散。存量记录里的旧文案不受影响：这一栏是
 * 自由文本，建议项只影响新上报时的下拉内容，不做任何映射或迁移。
 */
export const ADDRESS_ABNORMAL_REASON_SUGGESTIONS = [
  'pickup.addressReviewPool.abnormalReason.highwayOrBridge',
  'pickup.addressReviewPool.abnormalReason.homeWarehouseNoUnit',
  'pickup.addressReviewPool.abnormalReason.publicPlace',
  'pickup.addressReviewPool.abnormalReason.outOfPickupScope',
] as const;
